import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, Mail, ArrowRight, ArrowLeft, Loader2, Eye, EyeOff, Shield, Quote } from 'lucide-react';
import api, { login } from '../lib/api';

const TESTIMONIALS = [
  {
    quote: "Time constraint is the biggest X factor in our Business, Absolin Completed our ERP Project Efficiently in time.",
    company: "Sri Luxmi Tulasi Agro",
  },
  {
    quote: "We are experiencing exponential growth without increasing our headcount because of partner company like Absolin in ERP Implementation.",
    company: "Wheelsmart",
  },
  {
    quote: "Absolin helped our remote workforce get aligned with our business goals & requirements easily, this is a complete software.",
    company: "Sravan Shipping",
  },
  {
    quote: "Now we can track all our orders and look at transportation status quickly and easily communicate to our customers.",
    company: "Sri Lalitha",
  },
  {
    quote: "Focus ERP allows us to seamlessly navigate the intersection of our online sales. It's Great.",
    company: "WHY NOT",
  },
];

const STATS = [
  { value: 0, label: "Clients Served", key: "clients" },
  { value: "21", label: "Industries" },
  { value: "50+", label: "Specialists" },
  { value: "10+", label: "Years" },
];

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [displayCount, setDisplayCount] = useState(100);
  const navigate = useNavigate();

  // Animate counter from 100 to 180
  useEffect(() => {
    const target = 180;
    const step = Math.max(1, Math.ceil((target - 100) / 30));
    const timer = setInterval(() => {
      setDisplayCount(prev => {
        const next = prev + step;
        if (next >= target) { clearInterval(timer); return target; }
        return next;
      });
    }, 30);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please fill in all fields'); return; }
    setIsLoading(true);
    try {
      const response = await login(email, password);
      const data = response.data;
      localStorage.setItem('auth_token', data.token);
      const user = { email: data.email, name: data.name, role: data.role, assigned_verticals: data.assigned_verticals, user_type: data.user_type, can_access_collections: data.can_access_collections, can_access_presales: data.can_access_presales };
      localStorage.setItem('user_data', JSON.stringify(user));
      toast.success(`Welcome back, ${data.name || email}!`);
      navigate('/');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Login failed';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const t = TESTIMONIALS[activeTestimonial];

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        <div className="absolute inset-0 bg-[#050a18]" />
        {/* Ambient glows */}
        <div className="absolute top-[-20%] right-[-10%] w-[500px] h-[500px] bg-[#0066CC]/20 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-15%] left-[-5%] w-[400px] h-[400px] bg-cyan-500/15 rounded-full blur-[100px]" style={{ animationDelay: '1s', animationDuration: '4s' }} />
        <div className="absolute top-[40%] left-[30%] w-[250px] h-[250px] bg-blue-400/10 rounded-full blur-[80px]" style={{ animationDelay: '2s', animationDuration: '5s' }} />
        {/* AI Circuit grid */}
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
        {/* Neural connection dots */}
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: `radial-gradient(circle, rgba(0,102,204,0.6) 1.5px, transparent 1.5px)`,
          backgroundSize: '60px 60px',
          backgroundPosition: '30px 30px'
        }} />
        {/* Diagonal circuit traces */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="circuit" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
              <path d="M0,60 L30,60 L40,40 L80,40 L90,60 L120,60" fill="none" stroke="white" strokeWidth="1"/>
              <path d="M60,0 L60,30 L40,40" fill="none" stroke="white" strokeWidth="1"/>
              <path d="M60,120 L60,90 L80,80" fill="none" stroke="white" strokeWidth="1"/>
              <circle cx="40" cy="40" r="2.5" fill="rgba(0,200,255,0.5)"/>
              <circle cx="80" cy="40" r="2.5" fill="rgba(0,200,255,0.5)"/>
              <circle cx="60" cy="30" r="2" fill="rgba(0,102,204,0.4)"/>
              <circle cx="60" cy="90" r="2" fill="rgba(0,102,204,0.4)"/>
              <circle cx="80" cy="80" r="2" fill="rgba(0,200,255,0.3)"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#circuit)"/>
        </svg>
        {/* Floating AI particles */}
        <div className="absolute w-2 h-2 rounded-full bg-cyan-400/30 top-[15%] left-[20%] animate-ping" style={{ animationDuration: '3s' }} />
        <div className="absolute w-1.5 h-1.5 rounded-full bg-blue-400/40 top-[35%] right-[25%] animate-ping" style={{ animationDuration: '4s', animationDelay: '1s' }} />
        <div className="absolute w-2 h-2 rounded-full bg-cyan-300/20 bottom-[30%] left-[45%] animate-ping" style={{ animationDuration: '5s', animationDelay: '2s' }} />
        <div className="absolute w-1 h-1 rounded-full bg-blue-300/50 top-[55%] left-[15%] animate-ping" style={{ animationDuration: '3.5s', animationDelay: '0.5s' }} />

        <div className="relative z-10 flex flex-col justify-between w-full p-10 xl:p-14">
          {/* Logo */}
          <div>
            <img src="/absolin-logo-hd.png" alt="ABSOLIN" className="h-12 xl:h-14 w-auto object-contain" data-testid="login-brand-logo" />
          </div>

          {/* Main message */}
          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-[11px] text-blue-300 uppercase tracking-[0.2em] font-medium mb-5">
              <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" />
              Problem Solver Platform
            </div>
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-[1.15] tracking-tight">
              Transform Your<br />
              <span className="bg-gradient-to-r from-[#0066CC] via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Business Digitally
              </span>
            </h1>
            <p className="text-white/50 text-base mt-4 leading-relaxed max-w-md">
              Access AI-powered ERP insights, industry solutions, and expert guidance tailored for your business growth.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-3 mt-7">
              {STATS.map((s) => (
                <div key={s.label} className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-3 text-center backdrop-blur-sm">
                  <p className="text-xl xl:text-2xl font-bold text-white">{s.key === "clients" ? `${displayCount}+` : s.value}</p>
                  <p className="text-[10px] text-white/40 font-medium uppercase tracking-wider mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Testimonial */}
          <div>
            <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5 backdrop-blur-sm max-w-lg" data-testid="login-testimonial">
              <Quote className="w-5 h-5 text-[#0066CC]/60 mb-2" />
              <p className="text-white/60 text-sm leading-relaxed italic min-h-[48px] transition-opacity duration-500">
                "{t.quote}"
              </p>
              <div className="flex items-center justify-between mt-3">
                <p className="text-white/80 text-xs font-semibold">{t.company}</p>
                <div className="flex gap-1.5">
                  {TESTIMONIALS.map((_, i) => (
                    <button key={i} onClick={() => setActiveTestimonial(i)}
                      className={`w-1.5 h-1.5 rounded-full transition-all ${i === activeTestimonial ? "bg-[#0066CC] w-4" : "bg-white/20"}`}
                      data-testid={`testimonial-dot-${i}`} />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-5">
              <p className="text-white/25 text-xs tracking-wide">
                &copy; {new Date().getFullYear()} ABSOLIN Software Solutions LLP
              </p>
              <div className="flex items-center gap-1.5 text-white/20 text-xs">
                <span>Visakhapatnam</span>
                <span className="text-white/10">|</span>
                <span>Hyderabad</span>
                <span className="text-white/10">|</span>
                <span>Chandigarh</span>
                <span className="text-white/10">|</span>
                <span>Raipur</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-[45%] flex flex-col bg-white relative overflow-hidden">
        {/* Subtle AI dot grid */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(circle, #0066CC 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }} />
        {/* Neural mesh lines */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.025]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="mesh" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M0,40 L40,0 L80,40 L40,80 Z" fill="none" stroke="#0066CC" strokeWidth="0.5"/>
              <circle cx="40" cy="0" r="2" fill="#0066CC" opacity="0.3"/>
              <circle cx="80" cy="40" r="2" fill="#0066CC" opacity="0.3"/>
              <circle cx="40" cy="80" r="2" fill="#0066CC" opacity="0.3"/>
              <circle cx="0" cy="40" r="2" fill="#0066CC" opacity="0.3"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#mesh)"/>
        </svg>
        {/* Subtle gradient wash */}
        <div className="absolute top-[-20%] right-[-20%] w-[400px] h-[400px] bg-blue-100/30 rounded-full blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-15%] w-[300px] h-[300px] bg-cyan-50/40 rounded-full blur-[80px]" />

        <div className="flex-1 flex items-center justify-center relative z-10">
          <div className="w-full max-w-[400px] px-6 sm:px-8">
            {/* Mobile logo */}
            <div className="lg:hidden mb-10 text-center">
              <img src="/absolin-logo-transparent.png" alt="ABSOLIN" className="h-10 w-auto object-contain mx-auto mb-2" />
              <p className="text-slate-400 text-xs tracking-wider uppercase">Infinite Digital Possibilities</p>
            </div>

            {/* Back to website — for visitors who don't want to log in */}
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-[#0066CC] mb-6 group transition-colors"
              data-testid="login-back-home"
            >
              <ArrowLeft className="w-3.5 h-3.5 -translate-x-0 group-hover:-translate-x-0.5 transition-transform" />
              Back to website
            </Link>

            {/* Form header */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Your Digital Edge Starts Here</h2>
              <p className="text-slate-500 text-sm mt-1.5">Sign in to explore ERP insights, AI tools & industry solutions</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
              {/* Email */}
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-slate-600 text-xs font-semibold uppercase tracking-wider">Email</Label>
                <div className="relative group">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-300 group-focus-within:text-[#0066CC] transition-colors" />
                  <Input id="email" type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="pl-11 h-12 border-slate-200 bg-slate-50/50 focus:bg-white focus:border-[#0066CC] focus:ring-2 focus:ring-[#0066CC]/10 rounded-xl text-sm transition-all"
                    required data-testid="login-email" />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-600 text-xs font-semibold uppercase tracking-wider">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-[#0066CC] hover:text-blue-700 font-medium transition-colors" data-testid="forgot-password-link">Forgot Password?</Link>
                </div>
                <div className="relative group">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-300 group-focus-within:text-[#0066CC] transition-colors" />
                  <Input id="password" type={showPassword ? "text" : "password"} placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)}
                    className="pl-11 pr-11 h-12 border-slate-200 bg-slate-50/50 focus:bg-white focus:border-[#0066CC] focus:ring-2 focus:ring-[#0066CC]/10 rounded-xl text-sm transition-all"
                    required data-testid="login-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors" tabIndex={-1} data-testid="toggle-password-visibility">
                    {showPassword ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <Button type="submit"
                className="w-full h-12 bg-[#0066CC] hover:bg-[#0055aa] text-white font-semibold rounded-xl text-sm transition-all duration-200 shadow-lg shadow-[#0066CC]/20 hover:shadow-[#0066CC]/30 hover:translate-y-[-1px] active:translate-y-0"
                disabled={isLoading} data-testid="login-submit">
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <span className="flex items-center justify-center gap-2">Sign In <ArrowRight className="w-4 h-4" /></span>
                )}
              </Button>
            </form>

            {/* Security badge + visitor escape hatch */}
            <div className="mt-6 space-y-3">
              <div className="flex items-center justify-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-slate-300" />
                <p className="text-slate-400 text-xs">Protected by ABSOLIN Security</p>
              </div>
              <div className="flex items-center justify-center gap-4 text-xs">
                <Link to="/" className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="login-explore-instead">
                  Explore website
                </Link>
                <span className="w-px h-3 bg-slate-200"></span>
                <Link to="/schedule-demo" className="inline-flex items-center gap-1 text-[#0066CC] hover:text-blue-700 font-semibold transition-colors" data-testid="login-signup-link">
                  Sign Up <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Footer with Privacy Policy & T&C */}
        <div className="relative z-10 pb-5 pt-2 px-6 sm:px-8 text-center border-t border-slate-100" data-testid="login-footer">
          <p className="text-slate-400 text-[11px] mb-1">
            &copy; {new Date().getFullYear()} ABSOLIN Software Solutions LLP. All Rights Reserved.
          </p>
          <div className="flex items-center justify-center gap-1 text-[11px]">
            <a href="https://www.absolinsoft.com/privacy-policy/" target="_blank" rel="noopener noreferrer"
              className="text-slate-400 hover:text-[#0066CC] transition-colors" data-testid="privacy-policy-link">Privacy Policy</a>
            <span className="text-slate-300 mx-1">|</span>
            <a href="https://www.absolinsoft.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer"
              className="text-slate-400 hover:text-[#0066CC] transition-colors" data-testid="terms-conditions-link">Terms & Conditions</a>
          </div>
        </div>
      </div>
    </div>
  );
}
